// WDHandicapCalc.js
// Wild Dunes Course Handicap Calculator
// Route: /WDHandicapCalc
// 
// To add to TTO:
// 1. Save this file to: frontend/src/components/WDHandicapCalc.js
// 2. In App.js, add import: import WDHandicapCalc from './components/WDHandicapCalc';
// 3. In App.js, add route: <Route path="/WDHandicapCalc" element={<WDHandicapCalc />} />

import React, { useState, useEffect } from 'react';

// Wild Dunes course data - from official TTO course settings
const courseData = {
  links: {
    name: "Links",
    par: 70,
    tees: [
      { name: "Black", rating: 72.1, slope: 138, color: "#1a1a1a" },
      { name: "White", rating: 69.9, slope: 129, color: "#ffffff" },
      { name: "Blended (W/G)", rating: 68.1, slope: 127, color: "#f5f5dc" },
      { name: "Gold", rating: 66.4, slope: 121, color: "#d4af37" },
      { name: "Silver", rating: 63.9, slope: 117, color: "#c0c0c0" }
    ]
  },
  harbor: {
    name: "Harbor",
    par: 70,
    tees: [
      { name: "Black", rating: 71.6, slope: 137, color: "#1a1a1a" },
      { name: "White", rating: 68.9, slope: 123, color: "#ffffff" },
      { name: "Blended (W/G)", rating: 66.7, slope: 118, color: "#f5f5dc" },
      { name: "Gold", rating: 65.5, slope: 112, color: "#d4af37" },
      { name: "Red", rating: 62.9, slope: 106, color: "#cc3333" }
    ]
  }
};

// Styles object for inline styling (matching TTO patterns)
const styles = {
  container: {
    minHeight: '100vh',
    background: 'linear-gradient(135deg, #4a7c8c 0%, #2d5a66 100%)',
    padding: '20px',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'flex-start',
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Oxygen, Ubuntu, sans-serif'
  },
  card: {
    background: 'white',
    borderRadius: '20px',
    boxShadow: '0 10px 40px rgba(0,0,0,0.3)',
    padding: '25px',
    width: '100%',
    maxWidth: '380px',
    marginTop: '10px'
  },
  logoHeader: {
    textAlign: 'center',
    marginBottom: '20px'
  },
  logoText: {
    fontSize: '28px',
    fontWeight: '700',
    color: '#4a7c8c',
    letterSpacing: '-1px',
    margin: 0
  },
  logoSubtitle: {
    fontSize: '11px',
    color: '#888',
    textTransform: 'uppercase',
    letterSpacing: '2px',
    marginTop: '2px'
  },
  title: {
    textAlign: 'center',
    color: '#2d5a66',
    fontSize: '18px',
    margin: '0 0 25px 0',
    fontWeight: '500'
  },
  formGroup: {
    marginBottom: '18px'
  },
  label: {
    display: 'block',
    fontSize: '13px',
    fontWeight: '600',
    color: '#555',
    marginBottom: '6px',
    textTransform: 'uppercase',
    letterSpacing: '0.5px'
  },
  select: {
    width: '100%',
    padding: '14px 44px 14px 16px',
    fontSize: '18px',
    border: '2px solid #e0e0e0',
    borderRadius: '12px',
    background: '#fafafa',
    color: '#333',
    appearance: 'none',
    WebkitAppearance: 'none',
    backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='24' height='24' viewBox='0 0 24 24' fill='none' stroke='%23666' stroke-width='2'%3E%3Cpolyline points='6,9 12,15 18,9'%3E%3C/polyline%3E%3C/svg%3E")`,
    backgroundRepeat: 'no-repeat',
    backgroundPosition: 'right 12px center',
    backgroundSize: '20px',
    cursor: 'pointer',
    transition: 'all 0.2s ease'
  },
  input: {
    width: '100%',
    padding: '14px 16px',
    fontSize: '18px',
    border: '2px solid #e0e0e0',
    borderRadius: '12px',
    background: '#fafafa',
    color: '#333',
    boxSizing: 'border-box',
    transition: 'all 0.2s ease'
  },
  quickSelect: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '6px',
    marginTop: '8px'
  },
  quickBtn: {
    flex: '1',
    minWidth: '40px',
    padding: '8px 4px',
    fontSize: '13px',
    fontWeight: '600',
    color: '#4a7c8c',
    background: '#e8f4f8',
    border: 'none',
    borderRadius: '8px',
    cursor: 'pointer',
    transition: 'all 0.2s ease'
  },
  quickBtnHover: {
    background: '#4a7c8c',
    color: 'white'
  },
  resultBox: {
    background: 'linear-gradient(135deg, #4a7c8c 0%, #2d5a66 100%)',
    borderRadius: '16px',
    padding: '25px',
    textAlign: 'center',
    marginTop: '25px'
  },
  resultLabel: {
    color: 'rgba(255,255,255,0.85)',
    fontSize: '13px',
    textTransform: 'uppercase',
    letterSpacing: '2px',
    marginBottom: '8px'
  },
  resultNumber: {
    color: 'white',
    fontSize: '72px',
    fontWeight: '700',
    lineHeight: '1',
    textShadow: '0 2px 10px rgba(0,0,0,0.2)'
  },
  resultDetail: {
    color: 'rgba(255,255,255,0.75)',
    fontSize: '12px',
    marginTop: '12px'
  },
  ndbSection: {
    background: '#f5f5f0',
    borderRadius: '12px',
    padding: '15px',
    marginTop: '20px',
    textAlign: 'center'
  },
  ndbTitle: {
    fontSize: '11px',
    fontWeight: '600',
    color: '#666',
    textTransform: 'uppercase',
    letterSpacing: '1px',
    marginBottom: '8px'
  },
  ndbFormula: {
    fontSize: '16px',
    fontWeight: '700',
    color: '#2d5a66'
  },
  ndbExample: {
    fontSize: '11px',
    color: '#888',
    marginTop: '6px'
  },
  footer: {
    textAlign: 'center',
    marginTop: '20px',
    fontSize: '10px',
    color: '#aaa'
  }
};

const WDHandicapCalc = () => {
  const [course, setCourse] = useState('links');
  const [teeIndex, setTeeIndex] = useState(1); // Default to White tees
  const [handicapIndex, setHandicapIndex] = useState('');
  const [courseHandicap, setCourseHandicap] = useState(null);
  const [hoveredBtn, setHoveredBtn] = useState(null);

  // Calculate course handicap whenever inputs change
  useEffect(() => {
    if (handicapIndex === '' || isNaN(parseFloat(handicapIndex))) {
      setCourseHandicap(null);
      return;
    }

    const index = parseFloat(handicapIndex);
    const tee = courseData[course].tees[teeIndex];
    const par = courseData[course].par;

    // WHS Formula: Course Handicap = Index × (Slope / 113) + (Rating - Par)
    const ch = Math.round(index * (tee.slope / 113) + (tee.rating - par));
    setCourseHandicap(ch);
  }, [course, teeIndex, handicapIndex]);

  // Reset tee index when course changes
  useEffect(() => {
    setTeeIndex(1); // Reset to White tees
  }, [course]);

  const setQuickIndex = (value) => {
    setHandicapIndex(value.toString());
  };

  const quickIndexes = [5, 10, 15, 20, 25, 30];
  const currentTee = courseData[course].tees[teeIndex];

  return (
    <div style={styles.container}>
      <div style={styles.card}>
        {/* Logo Header */}
        <div style={styles.logoHeader}>
          <h1 style={styles.logoText}>Wild Dunes</h1>
          <div style={styles.logoSubtitle}>Charleston's Island Resort</div>
        </div>

        <h2 style={styles.title}>Course Handicap Calculator</h2>

        {/* Course Selection */}
        <div style={styles.formGroup}>
          <label style={styles.label}>Course</label>
          <select
            style={styles.select}
            value={course}
            onChange={(e) => setCourse(e.target.value)}
          >
            <option value="links">Links Course</option>
            <option value="harbor">Harbor Course</option>
          </select>
        </div>

        {/* Tee Selection */}
        <div style={styles.formGroup}>
          <label style={styles.label}>Tees</label>
          <select
            style={styles.select}
            value={teeIndex}
            onChange={(e) => setTeeIndex(parseInt(e.target.value))}
          >
            {courseData[course].tees.map((tee, index) => (
              <option key={index} value={index}>
                {tee.name} ({tee.rating}/{tee.slope})
              </option>
            ))}
          </select>
        </div>

        {/* Handicap Index Input */}
        <div style={styles.formGroup}>
          <label style={styles.label}>Handicap Index</label>
          <input
            type="number"
            style={styles.input}
            step="0.1"
            min="0"
            max="54"
            placeholder="Enter your index"
            value={handicapIndex}
            onChange={(e) => setHandicapIndex(e.target.value)}
          />
          <div style={styles.quickSelect}>
            {quickIndexes.map((idx) => (
              <button
                key={idx}
                style={{
                  ...styles.quickBtn,
                  ...(hoveredBtn === idx ? styles.quickBtnHover : {})
                }}
                onMouseEnter={() => setHoveredBtn(idx)}
                onMouseLeave={() => setHoveredBtn(null)}
                onClick={() => setQuickIndex(idx)}
              >
                {idx}
              </button>
            ))}
          </div>
        </div>

        {/* Result Box */}
        <div style={styles.resultBox}>
          <div style={styles.resultLabel}>Your Course Handicap</div>
          <div style={styles.resultNumber}>
            {courseHandicap !== null ? courseHandicap : '--'}
          </div>
          <div style={styles.resultDetail}>
            {courseHandicap !== null
              ? `${courseData[course].name} • ${currentTee.name} Tees`
              : 'Enter your Handicap Index'}
          </div>
        </div>

        {/* Net Double Bogey Section */}
        <div style={styles.ndbSection}>
          <div style={styles.ndbTitle}>Max Score Per Hole (Net Double Bogey)</div>
          <div style={styles.ndbFormula}>Par + 2 + Strokes You Get</div>
          <div style={styles.ndbExample}>Example: Par 4 + 2 + 1 stroke = 7 max</div>
        </div>

        {/* Footer */}
        <div style={styles.footer}>
          Use Course Handicap (not Index) for strokes received
          <div style={{ marginTop: '8px' }}>
            Brought to you by <a href="https://teetimeorganizer.net" style={{ color: '#4a7c8c', textDecoration: 'none' }}>TeeTimeOrganizer.net</a>
          </div>
        </div>
      </div>
    </div>
  );
};

export default WDHandicapCalc;
